import { Component, ErrorInfo, ReactNode } from 'react'
import { Toast } from 'antd-mobile'
import { CloseCircleFill, RedoOutline } from 'antd-mobile-icons'
import SafeArea from '@/components/SafeArea'
import { utils, webviewFun, i18n } from '@/utils'
import styles from './index.module.less'

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: '1em', height: '1em', display: 'inline-block', verticalAlign: 'middle' }}
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

export interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode | ((props: { error: Error; resetError: () => void }) => ReactNode)
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

export interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  errorTime: string
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorTime: '',
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const now = new Date()
    const timeStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate(),
    ).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(
      now.getMinutes(),
    ).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`

    return {
      hasError: true,
      error,
      errorTime: timeStr,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary caught error]:', error, errorInfo)
    this.setState({
      errorInfo,
    })
    this.props.onError?.(error, errorInfo)
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorTime: '',
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  formatErrorInfo = () => {
    const { error, errorInfo, errorTime } = this.state
    const url = window.location.href
    const userAgent = navigator.userAgent
    const errorMessage = error?.message || String(error)
    const errorStack = error?.stack || ''
    const componentStack = errorInfo?.componentStack || ''

    return [
      `=== Error Report ===`,
      `Time: ${errorTime || new Date().toISOString()}`,
      `URL: ${url}`,
      `UserAgent: ${userAgent}`,
      `Error Name: ${error?.name || 'Error'}`,
      `Error Message: ${errorMessage}`,
      `\n--- Error Stack ---`,
      errorStack,
      componentStack ? `\n--- Component Stack ---${componentStack}` : '',
    ]
      .filter(Boolean)
      .join('\n')
  }

  handleCopyError = async () => {
    const errorText = this.formatErrorInfo()
    const successTip = i18n.t('复制成功') || 'Copied successfully'

    try {
      if (webviewFun.isInApp?.()) {
        await webviewFun.copyNew(successTip, errorText)
        Toast.show({
          content: successTip,
          position: 'center',
        })
        return
      }

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(errorText)
        Toast.show({
          content: successTip,
          position: 'center',
        })
        return
      }

      utils.copyToClipboard(errorText)
      Toast.show({
        content: successTip,
        position: 'center',
      })
    } catch (e) {
      console.warn('Copy via standard API failed, fallback to helper:', e)
      try {
        utils.copyToClipboard(errorText)
        Toast.show({
          content: successTip,
          position: 'center',
        })
      } catch (err) {
        Toast.show({
          content: 'Copy failed',
          position: 'center',
        })
      }
    }
  }

  render() {
    const { hasError, error, errorInfo, errorTime } = this.state
    const { children, fallback } = this.props

    if (hasError && error) {
      if (typeof fallback === 'function') {
        return fallback({ error, resetError: this.handleReset })
      }
      if (fallback) {
        return fallback
      }

      return (
        <div className={styles['error-boundary-wrapper']}>
          <div>
            <SafeArea position="top" />
            <div className={styles['error-boundary-card']}>
              <div className={styles['error-icon-wrap']}>
                <CloseCircleFill />
              </div>

              <div className={styles['error-title']}>
                {i18n.t('页面出现错误') || 'Oops, something went wrong'}
              </div>

              <div className={styles['error-subtitle']}>
                {i18n.t('点击复制错误信息以便快速排查解决') ||
                  'You can copy the error details below to help us troubleshoot'}
              </div>

              <div className={styles['error-details-box']}>
                <div className={styles['error-details-header']}>
                  <span className={styles['error-tag']}>{error.name || 'Error'}</span>
                  {errorTime && <span className={styles['error-time']}>{errorTime}</span>}
                </div>

                <div className={styles['error-message']}>{error.message || String(error)}</div>

                {(error.stack || errorInfo?.componentStack) && (
                  <details className={styles['error-stack-details']}>
                    <summary>{i18n.t('查看调用栈详情') || 'View Call Stack'}</summary>
                    <pre>{(error.stack || '') + (errorInfo?.componentStack || '')}</pre>
                  </details>
                )}
              </div>

              <div className={styles['error-actions']}>
                <button
                  type="button"
                  className={`${styles['action-btn']} ${styles['copy-btn']}`}
                  onClick={this.handleCopyError}
                >
                  <CopyIcon className={styles['btn-icon']} />
                  <span>{i18n.t('复制错误信息') || 'Copy Error'}</span>
                </button>

                <button
                  type="button"
                  className={`${styles['action-btn']} ${styles['retry-btn']}`}
                  onClick={this.handleReload}
                >
                  <RedoOutline className={styles['btn-icon']} />
                  <span>{i18n.t('刷新重试') || 'Reload Page'}</span>
                </button>
              </div>
            </div>
          </div>

          <SafeArea position="bottom" />
        </div>
      )
    }

    return children
  }
}

export default ErrorBoundary
